/**
 * @zendir/ui - ActivityPlanner Component
 *
 * Activity scheduling and management panel. Creates, edits, and monitors
 * scheduled activities across timelines. Integrates with MissionCalendar
 * and Timeline components.
 *
 * Space Mission Features:
 * - Create/edit activities with command or script execution
 * - Drag-and-drop reorder within timeline
 * - Hazardous activity warnings (Astro UX)
 * - Activity lifecycle tracking (created → scheduled → started → completed)
 * - Overlap detection within a timeline
 * - Metadata recording (operator, environment, notes)
 * - Contact window awareness
 * - Batch scheduling
 *
 * @example
 * ```tsx
 * <ActivityPlanner
 *   activities={activities}
 *   timelines={timelines}
 *   onActivityCreate={(a) => console.log('Create:', a)}
 *   onActivityUpdate={(a) => console.log('Update:', a)}
 * />
 * ```
 */

import React, { useState, useMemo, useCallback, memo, useRef } from "react";
import { useTheme } from "../../theme";
import type {
  CalendarEvent,
  CalendarTimeline,
  ActivityStatus,
  ActivityType,
} from "./MissionCalendar";

// =============================================================================
// Types
// =============================================================================

export interface ActivityFormData {
  title: string;
  type: ActivityType;
  start: string; // ISO string
  end: string; // ISO string
  timeline: string;
  description: string;
  command: string;
  script: string;
  hazardous: boolean;
  metadata: Record<string, string>;
}

export interface ActivityPlannerProps {
  /** Activities to display */
  activities: CalendarEvent[];
  /** Available timelines */
  timelines: CalendarTimeline[];
  /** Currently selected date for scheduling */
  selectedDate?: Date;
  /** Height (default: 500) */
  height?: number | string;
  /** Title */
  title?: string;
  /** Read-only mode */
  readOnly?: boolean;
  /** Show overlap warnings */
  showOverlapWarning?: boolean;
  /** Called on create */
  onActivityCreate?: (activity: Partial<CalendarEvent>) => void;
  /** Called on update */
  onActivityUpdate?: (id: string, updates: Partial<CalendarEvent>) => void;
  /** Called on delete */
  onActivityDelete?: (id: string) => void;
  /** Called when requesting to view activity in calendar */
  onViewInCalendar?: (activity: CalendarEvent) => void;
  /** Called when requesting to view in timeline */
  onViewInTimeline?: (activity: CalendarEvent) => void;
  /** CSS class */
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Map activity status → Astro UX status color using tokens.
 *   created/scheduled → standby (cyan)
 *   started/completed → normal (green)
 *   paused/stopped/disabled → caution (yellow)
 *   error/failed/crashed → critical (red)
 */
function getStatusColor(
  status?: ActivityStatus,
  tokens?: ReturnType<typeof useTheme>["tokens"],
): string {
  if (!status || !tokens) return tokens?.colors.text.muted ?? "currentColor";
  switch (status) {
    case "created":
    case "scheduled":
      return tokens.colors.status.standby;
    case "started":
    case "completed":
      return tokens.colors.status.normal;
    case "paused":
    case "stopped":
    case "disabled":
      return tokens.colors.status.caution;
    case "error":
    case "failed":
    case "crashed":
      return tokens.colors.status.critical;
  }
}

/**
 * Return Astro UX status shape + contextual icon.
 * The leading character is the Astro status shape for the mapped level:
 *   standby → ◯ (ring), normal → ● (filled circle),
 *   caution → ■ (square), critical → ▼ (triangle down)
 * Followed by a contextual glyph for the specific activity state.
 */
function getStatusIcon(status?: ActivityStatus): string {
  if (!status) return "◯";
  switch (status) {
    case "created":
      return "◯"; // standby ring
    case "scheduled":
      return "◯ ◉"; // standby ring + scheduled
    case "started":
      return "● ▶"; // normal circle + play
    case "completed":
      return "● ✓"; // normal circle + check
    case "paused":
      return "■ ⏸"; // caution square + pause
    case "stopped":
      return "■ ⏹"; // caution square + stop
    case "disabled":
      return "■ ⊘"; // caution square + disabled
    case "error":
    case "failed":
    case "crashed":
      return "▼"; // critical triangle down
  }
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function checkOverlap(a: CalendarEvent, b: CalendarEvent): boolean {
  if (!a.end || !b.end) return false;
  if (a.timeline !== b.timeline) return false;
  return a.start < b.end && b.start < a.end;
}

// =============================================================================
// Form Component
// =============================================================================

const ActivityForm = memo(function ActivityForm({
  timelines,
  selectedDate,
  tokens,
  onSubmit,
  onCancel,
}: {
  timelines: CalendarTimeline[];
  selectedDate?: Date;
  tokens: ReturnType<typeof useTheme>["tokens"];
  onSubmit: (data: ActivityFormData) => void;
  onCancel: () => void;
}) {
  const now = selectedDate || new Date();
  const endDefault = new Date(now.getTime() + 3600000);

  const [form, setForm] = useState<ActivityFormData>({
    title: "",
    type: "command",
    start: now.toISOString().slice(0, 16),
    end: endDefault.toISOString().slice(0, 16),
    timeline: timelines[0]?.id || "",
    description: "",
    command: "",
    script: "",
    hazardous: false,
    metadata: {},
  });
  const [metaKey, setMetaKey] = useState("");
  const [metaValue, setMetaValue] = useState("");

  const addMetadata = useCallback(() => {
    if (metaKey.trim()) {
      setForm((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, [metaKey.trim()]: metaValue },
      }));
      setMetaKey("");
      setMetaValue("");
    }
  }, [metaKey, metaValue]);

  const inputStyle: React.CSSProperties = {
    background: tokens.colors.background.base,
    color: tokens.colors.text.primary,
    border: `1px solid ${tokens.colors.border.muted}`,
    borderRadius: tokens.borderRadius.md,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
    fontSize: tokens.typography.fontSize.xs,
    width: "100%",
    minHeight: 40,
    fontFamily: tokens.typography.fontFamily.primary,
    transition: "all 150ms ease",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: tokens.typography.fontSize.xxs,
    fontWeight: 500,
    color: tokens.colors.text.muted,
    textTransform: "uppercase",
    marginBottom: 3,
    display: "block",
  };

  return (
    <div
      style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}
    >
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 }}
      >
        <div>
          <label htmlFor="activity-title" style={labelStyle}>
            Title
          </label>
          <input
            id="activity-title"
            aria-label="Activity title"
            style={inputStyle}
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Activity name"
          />
        </div>
        <div>
          <label htmlFor="activity-type" style={labelStyle}>
            Type
          </label>
          <select
            id="activity-type"
            aria-label="Activity type"
            style={inputStyle}
            value={form.type}
            onChange={(e) =>
              setForm((p) => ({ ...p, type: e.target.value as ActivityType }))
            }
          >
            <option value="command">Command</option>
            <option value="script">Script</option>
            <option value="reserve">Reserve</option>
            <option value="contact">Contact</option>
            <option value="note">Note</option>
            <option value="metadata">Metadata</option>
          </select>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label htmlFor="activity-start-time" style={labelStyle}>
            Start Time
          </label>
          <input
            id="activity-start-time"
            aria-label="Start time"
            type="datetime-local"
            style={{
              ...inputStyle,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
            value={form.start}
            onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="activity-end-time" style={labelStyle}>
            End Time
          </label>
          <input
            id="activity-end-time"
            aria-label="End time"
            type="datetime-local"
            style={{
              ...inputStyle,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
            value={form.end}
            onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))}
          />
        </div>
      </div>
      {timelines.length > 0 && (
        <div>
          <label htmlFor="activity-timeline" style={labelStyle}>
            Timeline
          </label>
          <select
            id="activity-timeline"
            aria-label="Timeline"
            style={inputStyle}
            value={form.timeline}
            onChange={(e) =>
              setForm((p) => ({ ...p, timeline: e.target.value }))
            }
          >
            {timelines.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {form.type === "command" && (
        <div>
          <label htmlFor="activity-command" style={labelStyle}>
            Command
          </label>
          <input
            id="activity-command"
            aria-label="Command"
            style={{
              ...inputStyle,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
            value={form.command}
            onChange={(e) =>
              setForm((p) => ({ ...p, command: e.target.value }))
            }
            placeholder='cmd("TARGET COMMAND")'
          />
        </div>
      )}
      {form.type === "script" && (
        <div>
          <label htmlFor="activity-script" style={labelStyle}>
            Script Path
          </label>
          <input
            id="activity-script"
            aria-label="Script path"
            style={{
              ...inputStyle,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
            value={form.script}
            onChange={(e) => setForm((p) => ({ ...p, script: e.target.value }))}
            placeholder="/scripts/ops/collect.rb"
          />
        </div>
      )}
      <div>
        <label htmlFor="activity-description" style={labelStyle}>
          Description
        </label>
        <textarea
          id="activity-description"
          aria-label="Description"
          style={{ ...inputStyle, minHeight: 48, resize: "vertical" }}
          value={form.description}
          onChange={(e) =>
            setForm((p) => ({ ...p, description: e.target.value }))
          }
          placeholder="Notes..."
        />
      </div>

      {/* Metadata KV */}
      <div>
        <div style={labelStyle}>Metadata</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
          <input
            aria-label="Metadata key"
            style={{ ...inputStyle, flex: 1 }}
            value={metaKey}
            onChange={(e) => setMetaKey(e.target.value)}
            placeholder="Key"
          />
          <input
            aria-label="Metadata value"
            style={{ ...inputStyle, flex: 1 }}
            value={metaValue}
            onChange={(e) => setMetaValue(e.target.value)}
            placeholder="Value"
          />
          <button
            onClick={addMetadata}
            style={{
              background: tokens.colors.interactive.default,
              color: tokens.colors.text.inverse,
              border: "none",
              borderRadius: tokens.borderRadius.md,
              padding: `0 ${tokens.spacing.sm}px`,
              fontSize: tokens.typography.fontSize.xs,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            +
          </button>
        </div>
        {Object.keys(form.metadata).length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {Object.entries(form.metadata).map(([k, v]) => (
              <span
                key={k}
                style={{
                  fontSize: tokens.typography.fontSize.micro,
                  background: tokens.colors.background.surface,
                  padding: `2px 6px`,
                  borderRadius: tokens.borderRadius.sm,
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.spacing.xs,
                }}
              >
                {k}: {v}
                <button
                  type="button"
                  aria-label={`Remove metadata ${k}`}
                  onClick={() =>
                    setForm((p) => {
                      const m = { ...p.metadata };
                      delete m[k];
                      return { ...p, metadata: m };
                    })
                  }
                  style={{
                    cursor: "pointer",
                    color: tokens.colors.status.critical,
                    fontWeight: 500,
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Hazardous toggle */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: tokens.typography.fontSize.xs,
          cursor: "pointer",
        }}
      >
        <input
          aria-label="Hazardous activity"
          type="checkbox"
          checked={form.hazardous}
          onChange={(e) =>
            setForm((p) => ({ ...p, hazardous: e.target.checked }))
          }
        />
        <span
          style={{
            color: form.hazardous
              ? tokens.colors.status.critical
              : tokens.colors.text.secondary,
          }}
        >
          ⚠ Hazardous Activity
        </span>
      </label>

      {/* Buttons */}
      <div
        style={{
          display: "flex",
          gap: 6,
          justifyContent: "flex-end",
          marginTop: 4,
        }}
      >
        <button
          onClick={onCancel}
          style={{
            background: "transparent",
            color: tokens.colors.text.muted,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            padding: `${tokens.spacing.xs}px 14px`,
            fontSize: tokens.typography.fontSize.xs,
            cursor: "pointer",
            transition: "all 150ms ease",
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit(form)}
          disabled={!form.title.trim()}
          style={{
            background: tokens.colors.interactive.default,
            color: tokens.colors.text.inverse,
            border: "none",
            borderRadius: tokens.borderRadius.md,
            padding: `${tokens.spacing.xs}px 14px`,
            fontSize: tokens.typography.fontSize.xs,
            cursor: form.title.trim() ? "pointer" : "not-allowed",
            fontWeight: 500,
            opacity: form.title.trim() ? 1 : 0.5,
            transition: "all 150ms ease",
          }}
        >
          Create Activity
        </button>
      </div>
    </div>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export const ActivityPlanner = memo(function ActivityPlanner({
  activities,
  timelines,
  selectedDate,
  height = 500,
  title = "Activity Planner",
  readOnly = false,
  showOverlapWarning = true,
  onActivityCreate,
  onActivityUpdate: _onActivityUpdate,
  onActivityDelete,
  onViewInCalendar,
  onViewInTimeline,
  className = "",
}: ActivityPlannerProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState<"time" | "status" | "timeline">("time");
  const listRef = useRef<HTMLDivElement>(null);

  // Overlap detection
  const overlaps = useMemo(() => {
    const pairs: [string, string][] = [];
    for (let i = 0; i < activities.length; i++) {
      for (let j = i + 1; j < activities.length; j++) {
        if (checkOverlap(activities[i], activities[j])) {
          pairs.push([activities[i].id, activities[j].id]);
        }
      }
    }
    return new Set(pairs.flat());
  }, [activities]);

  // Sort and filter
  const displayActivities = useMemo(() => {
    let list = [...activities];
    if (filter) {
      const lf = filter.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(lf) ||
          a.description?.toLowerCase().includes(lf) ||
          a.timeline?.toLowerCase().includes(lf) ||
          a.command?.toLowerCase().includes(lf),
      );
    }
    switch (sortBy) {
      case "time":
        list.sort((a, b) => a.start.getTime() - b.start.getTime());
        break;
      case "status":
        list.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
        break;
      case "timeline":
        list.sort((a, b) => (a.timeline || "").localeCompare(b.timeline || ""));
        break;
    }
    return list;
  }, [activities, filter, sortBy]);

  // Stats
  const stats = useMemo(
    () => ({
      total: activities.length,
      scheduled: activities.filter(
        (a) => a.status === "scheduled" || a.status === "created",
      ).length,
      running: activities.filter((a) => a.status === "started").length,
      completed: activities.filter((a) => a.status === "completed").length,
      errors: activities.filter(
        (a) =>
          a.status === "error" ||
          a.status === "failed" ||
          a.status === "crashed",
      ).length,
    }),
    [activities],
  );

  const handleCreate = useCallback(
    (data: ActivityFormData) => {
      onActivityCreate?.({
        title: data.title,
        type: data.type,
        start: new Date(data.start),
        end: new Date(data.end),
        timeline: data.timeline,
        description: data.description,
        command: data.command || undefined,
        script: data.script || undefined,
        metadata:
          Object.keys(data.metadata).length > 0 ? data.metadata : undefined,
        status: "created",
      });
      setShowForm(false);
    },
    [onActivityCreate],
  );

  const panelPadX = tokens.spacing.md;
  const panelPadY = tokens.spacing.sm;

  return (
    <div
      className={`activity-planner ${className}`}
      style={{
        height: typeof height === "number" ? height : undefined,
        background: tokens.colors.background.base,
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: `${panelPadY}px ${panelPadX}px`,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          background: tokens.colors.background.surface,
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
          }}
        >
          <span
            style={{ fontWeight: 500, fontSize: tokens.typography.fontSize.sm }}
          >
            {title}
          </span>
          <div
            style={{
              display: "flex",
              gap: 10,
              fontSize: tokens.typography.fontSize.xxs,
              color: tokens.colors.text.muted,
            }}
          >
            <span>Scheduled {stats.scheduled}</span>
            <span style={{ color: tokens.colors.text.secondary }}>
              Running {stats.running}
            </span>
            <span style={{ color: tokens.colors.text.secondary }}>
              Completed {stats.completed}
            </span>
            {stats.errors > 0 && (
              <span style={{ color: tokens.colors.status.critical }}>
                Errors {stats.errors}
              </span>
            )}
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              background: showForm
                ? tokens.colors.status.critical + "22"
                : tokens.colors.interactive.default,
              color: showForm
                ? tokens.colors.status.critical
                : tokens.colors.text.inverse,
              border: "none",
              borderRadius: tokens.borderRadius.md,
              padding: "3px 10px",
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            {showForm ? "✕ Cancel" : "+ New Activity"}
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && !readOnly && (
        <div
          style={{
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface + "88",
          }}
        >
          <ActivityForm
            timelines={timelines}
            selectedDate={selectedDate}
            tokens={tokens}
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Search / Sort */}
      <div
        style={{
          padding: `${tokens.spacing.xs}px ${panelPadX}px`,
          display: "flex",
          gap: 6,
          alignItems: "center",
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
        }}
      >
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search activities"
          placeholder="Search activities..."
          style={{
            flex: 1,
            background: tokens.colors.background.surface,
            color: tokens.colors.text.primary,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            padding: "3px 8px",
            fontSize: tokens.typography.fontSize.xxs,
            fontFamily: tokens.typography.fontFamily.primary,
            transition: "all 150ms ease",
          }}
        />
        <select
          value={sortBy}
          onChange={(e) =>
            setSortBy(e.target.value as "time" | "status" | "timeline")
          }
          aria-label="Sort activities"
          style={{
            background: tokens.colors.background.surface,
            color: tokens.colors.text.secondary,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            padding: "3px 6px",
            fontSize: tokens.typography.fontSize.xxs,
            transition: "all 150ms ease",
          }}
        >
          <option value="time">Sort: Time</option>
          <option value="status">Sort: Status</option>
          <option value="timeline">Sort: Timeline</option>
        </select>
      </div>

      {/* Activity list */}
      <div ref={listRef} style={{ flex: 1, overflow: "auto", padding: 8 }}>
        {displayActivities.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: tokens.colors.text.muted,
              padding: 40,
              fontSize: tokens.typography.fontSize.xs,
            }}
          >
            {filter ? "No matching activities" : "No activities scheduled"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {displayActivities.map((a) => {
              const color = a.color || getStatusColor(a.status, tokens);
              const hasOverlap = overlaps.has(a.id);
              return (
                <div
                  key={a.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "24px 1fr auto",
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                    padding: "6px 10px",
                    background: hasOverlap
                      ? `${tokens.colors.status.caution}10`
                      : `${color}08`,
                    borderLeft: `3px solid ${color}`,
                    borderRadius: tokens.borderRadius.md,
                    fontSize: tokens.typography.fontSize.xs,
                    cursor: "pointer",
                    border:
                      hasOverlap && showOverlapWarning
                        ? `1px solid ${tokens.colors.status.caution}44`
                        : undefined,
                    transition: "all 150ms ease",
                  }}
                >
                  {/* Status icon */}
                  <div style={{ textAlign: "center" }}>
                    <span
                      style={{
                        color,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: 500,
                      }}
                    >
                      {getStatusIcon(a.status)}
                    </span>
                  </div>
                  {/* Info */}
                  <div style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.title}
                      </span>
                      {a.timeline && (
                        <span
                          style={{
                            fontSize: tokens.typography.fontSize.micro,
                            background: tokens.colors.background.surface,
                            padding: `0 ${tokens.spacing.xs}px`,
                            borderRadius: tokens.borderRadius.sm,
                            color: tokens.colors.text.muted,
                          }}
                        >
                          {a.timeline}
                        </span>
                      )}
                      {hasOverlap && showOverlapWarning && (
                        <span
                          style={{
                            fontSize: tokens.typography.fontSize.micro,
                            color: tokens.colors.status.caution,
                            fontWeight: 500,
                          }}
                        >
                          ⚠ OVERLAP
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: tokens.typography.fontSize.micro,
                        fontFamily: tokens.typography.fontFamily.mono,
                        color: tokens.colors.text.muted,
                      }}
                    >
                      {formatDate(a.start)} {formatTime(a.start)}
                      {a.end ? ` → ${formatTime(a.end)}` : ""}
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: "flex", gap: 4 }}>
                    {onViewInCalendar && (
                      <button
                        aria-label={`View ${a.title} in calendar`}
                        onClick={() => onViewInCalendar(a)}
                        style={actionBtnStyle(tokens)}
                        title="View in Calendar"
                      >
                        Calendar
                      </button>
                    )}
                    {onViewInTimeline && (
                      <button
                        aria-label={`View ${a.title} in timeline`}
                        onClick={() => onViewInTimeline(a)}
                        style={actionBtnStyle(tokens)}
                        title="View in Timeline"
                      >
                        Timeline
                      </button>
                    )}
                    {!readOnly && onActivityDelete && (
                      <button
                        aria-label={`Delete ${a.title}`}
                        onClick={() => onActivityDelete(a.id)}
                        style={{
                          ...actionBtnStyle(tokens),
                          color: tokens.colors.status.critical,
                        }}
                        title="Delete"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: `${tokens.spacing.xs}px ${panelPadX}px`,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
          background: tokens.colors.background.surface,
          fontSize: tokens.typography.fontSize.micro,
          color: tokens.colors.text.muted,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>
          {displayActivities.length} / {activities.length} activities
        </span>
        {overlaps.size > 0 && showOverlapWarning && (
          <span style={{ color: tokens.colors.status.caution }}>
            ⚠ {overlaps.size} overlapping activities
          </span>
        )}
      </div>
    </div>
  );
});

function actionBtnStyle(
  tokens: ReturnType<typeof useTheme>["tokens"],
): React.CSSProperties {
  return {
    background: "transparent",
    border: `1px solid ${tokens.colors.border.muted}`,
    borderRadius: tokens.borderRadius.sm,
    padding: `2px ${tokens.spacing.xs}px`,
    fontSize: tokens.typography.fontSize.xxs,
    cursor: "pointer",
    lineHeight: 1,
    transition: "all 150ms ease",
  };
}

export default ActivityPlanner;
